/**
 * Typed testid registry for the `pages/` bucket — the SINGLE source of truth for
 * every `data-testid` the page-shell components render (TV1-2055 phase 3,
 * rolling out the TV1-1866 pattern to the web-app tail: `components/`, `pages/`,
 * `contexts/`).
 *
 * Pages spread these values into `data-testid`; the LIVE Playwright specs import
 * the SAME object for their selectors. A renamed key changes the string in ONE
 * place and both sides move together — instead of a runtime "element not found"
 * when a page testid drifts from the string a spec hardcoded (e2e is not run in
 * CI, so that drift fails silently). The `teselly/no-raw-testid` ESLint rule
 * forbids raw `data-testid` literals under `src/apps/web-app/**` so new testids
 * must land in a registry like this one.
 *
 * The `authError*Code` entries below are the `testId` values that
 * `SocialLoginError` selects by error code and passes into a dynamic
 * `data-testid={testId}`; they are registered here so the specs that assert on
 * them (`auth-social/ml-login.spec.ts`) share the same source.
 *
 * Naming convention: `<area>-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). Values are preserved verbatim from the
 * pre-existing literals (no rename) so the migration is value-safe.
 */
export const pagesTestIds = {
  /* TV1-2369 — the app-never-loaded fallback and its retry. */
  stalledLoadPage: 'pages-stalled-load',
  stalledLoadRetry: 'pages-stalled-load-retry',
  // --- Authentication / sign-in + sign-up form ---
  /** Name input (sign-up mode). */
  authFormNameInput: 'auth-form-name-input',
  /** Email input. */
  authFormEmailInput: 'auth-form-email-input',
  /** Password input. */
  authFormPasswordInput: 'auth-form-password-input',
  /** Sign-in error message. */
  authSigninError: 'auth-signin-error',
  /** Submit button. */
  authFormSubmitButton: 'auth-form-submit-button',
  /** "Forgot password" link. */
  authFormForgotLink: 'auth-form-forgot-link',
  /** Email-method sign-in option. */
  authSigninEmail: 'auth-signin-email',
  /** Password-method option. */
  authMethodPassword: 'auth-method-password',
  /** Google social-login button. */
  authSigninGoogle: 'auth-signin-google',
  /** MercadoLibre social-login button. */
  authSigninMercadolibre: 'auth-signin-mercadolibre',
  /** Sign-in / sign-up mode toggle. */
  authFormToggleMode: 'auth-form-toggle-mode',

  // --- Authentication / ForgotPassword ---
  /** Success confirmation message. */
  authForgotSuccessMessage: 'auth-forgot-success-message',
  /** Email input. */
  authForgotEmailInput: 'auth-forgot-email-input',
  /** Submit button. */
  authForgotSubmitButton: 'auth-forgot-submit-button',

  // --- Authentication / ResetPassword ---
  /** New-password input. */
  authResetNewPasswordInput: 'auth-reset-new-password-input',
  /** Confirm-password input. */
  authResetConfirmPasswordInput: 'auth-reset-confirm-password-input',
  /** Password-strength checklist. */
  authResetPasswordChecklist: 'auth-reset-password-checklist',
  /** Submit button. */
  authResetSubmitButton: 'auth-reset-submit-button',

  // --- Authentication / SocialLoginError ---
  /** Error title. */
  authErrorTitle: 'auth-error-title',
  /** Error message. */
  authErrorMessage: 'auth-error-message',
  /** Raw provider description (unknown-code passthrough). */
  authErrorDescription: 'auth-error-description',
  /** Back-to-login button. */
  authErrorBackButton: 'auth-error-back-button',
  /** Root testid for the ML email-not-verified error code. */
  authErrorMlEmailNotVerifiedCode: 'auth-error-ml-email-not-verified',
  /** Root testid for the ML account-already-linked error code. */
  authErrorMlAlreadyLinkedCode: 'auth-error-ml-already-linked',
  /** Root testid for the account-not-linked error code. */
  authErrorAccountNotLinkedCode: 'auth-error-account-not-linked',
  /** Root testid for the generic / unknown error code. */
  authErrorGenericCode: 'auth-error-generic',

  // --- Authentication / TwoFactorRequired ---
  /** Page root. */
  authTwoFactorRequired: 'auth-two-factor-required',
  /** Title. */
  authTwoFactorTitle: 'auth-two-factor-title',
  /** Message. */
  authTwoFactorMessage: 'auth-two-factor-message',
  /** Back button. */
  authTwoFactorBackButton: 'auth-two-factor-back-button',

  // --- Automation (page shell tabs) ---
  /** Tabs container. */
  automationTabs: 'automation-tabs',
  /** "Reglas" tab panel. */
  automationTabReglas: 'automation-tab-reglas',
  /** "Actividad" tab panel. */
  automationTabActividad: 'automation-tab-actividad',
  /** "Solicitudes" tab panel (TV1-2377 slice 7). */
  automationTabSolicitudes: 'automation-tab-solicitudes',

  // --- Billing ---
  /** Billing page wrapper. */
  billingPage: 'invoicing-billing-page',

  // --- Dashboard ---
  /** KPI row on the dashboard. */
  dashboardKpiRow: 'dashboard-kpi-row',
  /** Picks which saved report the Dashboard shows (the per-user pointer). */
  dashboardReportPicker: 'dashboard-report-picker',

  // --- ErrorPages / Forbidden403 ---
  /** 403 page root. */
  forbidden403: 'forbidden-403',

  // --- Integrations ---
  /** Integrations page wrapper. */
  integrationsPage: 'integrations-page',

  // --- _DevSpreadsheetBench (dev-only performance bench) ---
  /** Bench root container. */
  devBenchRoot: 'dev-spreadsheet-bench-root',
  /** Rows-count select. */
  devBenchRowsSelect: 'dev-spreadsheet-bench-rows-select',
  /** Columns-count select. */
  devBenchColumnsSelect: 'dev-spreadsheet-bench-columns-select',
  /** Seed input. */
  devBenchSeedInput: 'dev-spreadsheet-bench-seed-input',
  /** Long-strings switch. */
  devBenchLongStringsSwitch: 'dev-spreadsheet-bench-long-strings-switch',
  /** Variable-heights switch. */
  devBenchVariableHeightsSwitch: 'dev-spreadsheet-bench-variable-heights-switch',
  /** Regenerate button. */
  devBenchRegenerateButton: 'dev-spreadsheet-bench-regenerate-button',
  /** Save-to-backend button. */
  devBenchSaveButton: 'dev-spreadsheet-bench-save-button',
  /** Load-from-backend button. */
  devBenchLoadButton: 'dev-spreadsheet-bench-load-button',
  /** Seed-backend button. */
  devBenchSeedBackendButton: 'dev-spreadsheet-bench-seed-backend-button',
  /** Run-full-benchmark button. */
  devBenchRunBenchmarkButton: 'dev-spreadsheet-bench-run-benchmark-button',
  /** Scroll-FPS scenario button. */
  devBenchScenarioScrollFps: 'dev-spreadsheet-bench-scenario-scroll-fps',
  /** Per-edit scenario button. */
  devBenchScenarioPerEdit: 'dev-spreadsheet-bench-scenario-per-edit',
  /** Filter scenario button. */
  devBenchScenarioFilter: 'dev-spreadsheet-bench-scenario-filter',
  /** Ctrl-A scenario button. */
  devBenchScenarioCtrlA: 'dev-spreadsheet-bench-scenario-ctrl-a',
  /** Paste scenario button. */
  devBenchScenarioPaste: 'dev-spreadsheet-bench-scenario-paste',
  /** Benchmark result markdown block. */
  devBenchResultCode: 'dev-spreadsheet-bench-result-code',
  /** ScrollSpike variant A. */
  devBenchScrollSpikeVariantA: 'dev-spreadsheet-bench-scroll-spike-variant-a',
  /** ScrollSpike variant B. */
  devBenchScrollSpikeVariantB: 'dev-spreadsheet-bench-scroll-spike-variant-b'
} as const;
